import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EducationDetails } from '../../data/education-details';
import { NavigationService } from '../../services/navigation.service';
import { MatDialog } from '@angular/material/dialog';
import { QualificationDialogComponent } from '../../components/qualification-dialog/qualification-dialog.component';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { IFormTypes } from '../../data/form-types';
import { IFormComponent } from '../../models/i-form-component';
import { LookupItem } from '../../models/lookup-item';
import { AssessmentService } from '../../services/assessment.service';
import { ApplicantText } from '../../models/applicant-text';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';


@Component({
  selector: 'app-education',
  templateUrl: './education.component.html',
  styleUrl: './education.component.scss'
})
export class EducationComponent implements OnInit, OnDestroy, IFormComponent {

   @Input()formTypes!: IFormTypes;
   @Input()model!: InitialAssessmentForm;
   @Input()qualificationsList!:LookupItem[];
   @Input()countries!: LookupItem[];
   @Output() formReady = new EventEmitter<FormGroup>();

   submitAttempted: boolean = false;
isSubmitted = false;
  showSkillsSection = false;
  showLanguageSection = false;
  displayedColumns = ['qualification', 'course', 'institution', 'country', 'edit', 'delete'];
  englishTestTakenOptions = [
    { value: 'cae', label: 'CAE' },
    { value: 'esol', label: 'ESOL' },
    { value: 'ielts', label: 'IELTS' },
    { value: 'oet', label: 'OET' },
    { value: 'pte', label: 'Pearson Test' },
    { value: 'toeflIbt', label: 'TOEFL iBT' },
    { value: 'toeflPbt', label: 'TOEFL PBT' },
    { value: 'toeic', label: 'TOEIC' },
    { value: 'other', label: 'Other' }
  ];  

  formBuilder = inject(NonNullableFormBuilder);

  educationForm = this.formBuilder.group({
      hasQualifications: ['', [Validators.required]],
      completedEnglishTest: [''],
      englishTestTaken: [''],
      englishTestDate: [''],
      englishAverageScore: [''],
      englishReadingScore: [''],
      englishWritingScore: [''],
      englishSpeakingScore: [''],
      englishListeningScore: [''],
      isTranslator: [''],
      translatorLanguage: [''],
      skillsAssessment: [''],
      assessingAuthority: [''],
      assessedOccupation: [''],
      assessmentDate: ['']

  },{
     validators: requiredListWhenYesValidator([
            {
              controlName: 'hasQualifications',
              errorKey: 'qualificationsRequired',
              getItems: () => this.model?.education?.qualifications ?? []
            }])
  });

  applicantText: ApplicantText;

  constructor(
    private dialog: MatDialog,
    private assessmentService: AssessmentService
    
  ) {
    this.applicantText = this.assessmentService.applicantText();

    this.showSkillsSection = !this.assessmentService.isStudy() && 
                             !this.assessmentService.isBusiness();
    this.showLanguageSection = this.assessmentService.isSkilled();
  }

    private destroy$ = new Subject<void>();
    private breakpointObserver = inject(BreakpointObserver);
    isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
      map(result => result.matches),
      takeUntil(this.destroy$)
    );
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  ngOnInit(): void {

    this.model = this.model || new InitialAssessmentForm();
    this.model.education = this.model.education || new EducationDetails();

     // Bind form to model data
    this.educationForm.patchValue(this.model.education);
    
    // Subscribe to form changes to update model
    this.educationForm.valueChanges.subscribe(value => {
      this.model.education = {
        ...this.model.education!,
        ...value
      }
    });
  }


  add(): void {
    const dialogRef = this.dialog.open(QualificationDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { qualification: null, qualificationLevels: this.qualificationsList, countries: this.countries, applicantText: this.applicantText }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.education!.qualifications.push(result);
        this.model.education!.qualifications = [...this.model.education!.qualifications];
        this.educationForm.controls.hasQualifications.updateValueAndValidity();
      }
    });
  }

  remove(index: number): void {
    this.model.education!.qualifications.splice(index, 1);
    this.model.education!.qualifications = [...this.model.education!.qualifications];
    this.educationForm.controls.hasQualifications.updateValueAndValidity();
  }

edit(index: number): void {
    const qualification = this.model.education!.qualifications[index];
    const dialogRef = this.dialog.open(QualificationDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
      data: { qualification: qualification, qualificationLevels: this.qualificationsList, countries: this.countries, applicantText: this.applicantText }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.education!.qualifications[index] = result;
        this.model.education!.qualifications = [...this.model.education!.qualifications];
        this.educationForm.controls.hasQualifications.updateValueAndValidity();
      }
    });
  }

  isFormValid(): boolean {
    this.submitAttempted = true;
    return this.educationForm.valid;
  }

  markFormAsTouched(): void {
    this.educationForm.markAllAsTouched();
  }

}
